/**
 * WAFR review (agentic loop) endpoints — scope form, evidence sweep, item
 * confirm/answer/reopen/remediation, session chat, and the deterministic
 * report download. Mirrors backend api/wafr_review.py, mounted under
 * /api/companies/{slug}/wafr-review.
 */
import { ApiError, isApiStatus, request, requestBlob, saveBlob, type RequestOptions } from './core'
import type {
  AceSearchResponse,
  WafrFundingOutcomeStatus,
  WafrFundingRequest,
  WafrReviewMeta,
  WafrReviewState,
} from './types'

/** A publish refused for its target account (409 workload_account_required |
 *  workload_account_invalid) → an Error carrying just the server's actionable
 *  message; anything else → null (rethrown unchanged). Branches on the
 *  `ApiError`'s status + raw body, never on the message text. */
export function workloadAccountError(e: unknown): Error | null {
  if (!isApiStatus(e, 409)) return null
  try {
    const detail = JSON.parse((e as ApiError).body)?.detail
    if (typeof detail?.error === 'string' && detail.error.startsWith('workload_account_')) {
      return new Error(String(detail.message))
    }
  } catch {
    // not a JSON body — not ours
  }
  return null
}

/** Keep whichever review is newer. Poll refreshes and mutation responses race
 *  (a GET issued at revision 7 can land after a confirm already set 8), so a
 *  response with a LOWER revision than the one on screen is stale and dropped —
 *  adopting it would roll item versions back and 412 the next edit. An equal
 *  revision is adopted: session appends and evidence inserts don't bump it. */
export function newerReview(
  prev: WafrReviewState | null,
  next: WafrReviewState,
): WafrReviewState {
  return prev && next.revision < prev.revision ? prev : next
}

/** PUT /funding — only the keys sent apply (partial patch). `status` narrows to
 *  the outcome states: the endpoint 422s on 'none'. */
export type WafrFundingPatch = Partial<
  Pick<
    WafrFundingRequest,
    | 'activity_name'
    | 'business_description'
    | 'planned_start_date'
    | 'planned_end_date'
    | 'arr_usd'
    | 'fund_request_id'
    | 'notes'
  >
> & { status?: WafrFundingOutcomeStatus }

const base = (slug: string) => `/api/companies/${slug}/wafr-review`

/** POST to a review endpoint; `body` is JSON-encoded when given. */
function post<T>(slug: string, path: string, body?: unknown, opts?: RequestOptions) {
  return request<T>(
    `${base(slug)}${path}`,
    body === undefined ? { method: 'POST' } : { method: 'POST', body: JSON.stringify(body) },
    opts,
  )
}

export const wafrReviewApi = {
  getWafrReview: (slug: string) => request<WafrReviewState>(base(slug)),

  /** Open ACE opportunities matching `query`. Never throws on an AWS-side error —
   *  the message comes back in `error` for the modal to show inline. */
  searchWafrAce: (slug: string, query = '') =>
    post<AceSearchResponse>(slug, `/ace/search?q=${encodeURIComponent(query)}`),

  /** Record an existing opportunity on the review. */
  linkWafrAce: (slug: string, opportunityId: string, revision: number) =>
    post<WafrReviewState>(slug, '/ace/link', { opportunity_id: opportunityId }, { ifMatch: revision }),

  /** Create a WAFR-shaped opportunity (`WAFR-<CustomerName>`, APN program
   *  `Well-Architected`, $1 MRR) and record it on the review. */
  createWafrAce: (slug: string, revision: number) =>
    post<{ opportunity_id: string; state: WafrReviewState }>(slug, '/ace/opportunity', undefined, {
      ifMatch: revision,
    }),

  /** Push the published workload ARN onto the linked opportunity. */
  syncWafrAceArn: (slug: string, revision: number) =>
    post<WafrReviewState>(slug, '/ace/sync-arn', undefined, { ifMatch: revision }),

  /** Manually (re-)prepare the WAFP fund request — the publish worker does this
   *  automatically. 409 funding_not_ready before a workload ARN exists. */
  prepareWafrFunding: (slug: string, revision: number) =>
    post<WafrReviewState>(slug, '/funding/prepare', undefined, { ifMatch: revision }),

  /** Edit the prepared fund request / record the APFP outcome (partial patch). */
  updateWafrFunding: (slug: string, patch: WafrFundingPatch, revision: number) =>
    request<WafrReviewState>(
      `${base(slug)}/funding`,
      { method: 'PUT', body: JSON.stringify(patch) },
      { ifMatch: revision },
    ),

  /** Create the review (409 if one already exists). The POST body IS the
   *  scope-fields object, not wrapped. */
  createWafrReview: (slug: string, fields?: Record<string, unknown>) =>
    post<WafrReviewState>(slug, '', fields ?? {}),

  /** Light poll target — the workspace refetches the full review only when it changes. */
  getWafrReviewMeta: (slug: string) => request<WafrReviewMeta>(`${base(slug)}/meta`),

  /** Save changed scope fields only. */
  saveWafrReviewFields: (slug: string, fields: Record<string, unknown>, revision: number) =>
    request<WafrReviewState>(
      `${base(slug)}/fields`,
      { method: 'PUT', body: JSON.stringify({ fields }) },
      { ifMatch: revision },
    ),

  /** Kick the evidence sweep (409 while already running). `force` restarts a
   *  sweep stuck in `running` by a dead worker. */
  kickWafrSweep: (slug: string, revision: number, force = false) =>
    post<{ sweep_status: string }>(slug, '/sweep', { force }, { ifMatch: revision }),

  /** Publish the review into the customer's WA Tool (202; publish state rides
   *  GET ''). 409 publish_running while one is in flight; `force` restarts a
   *  publish stranded in `running` by a dead worker (the WA Tool writes are
   *  idempotent). `workloadAccountId` is required when the connection has
   *  several accounts (409 workload_account_required|invalid otherwise). */
  publishWafrToWaTool: (
    slug: string,
    revision: number,
    force = false,
    workloadAccountId?: string,
  ) =>
    post<{ wa_publish_status: string }>(
      slug,
      '/wa-tool/publish',
      workloadAccountId ? { force, workload_account_id: workloadAccountId } : { force },
      { ifMatch: revision },
    ).catch((e: unknown) => {
      throw workloadAccountError(e) ?? e
    }),

  // Item mutations are ITEM-scoped: they send the `version` of the item the
  // client saw (`X-Item-Version`), not the review revision — the sweep and
  // workers bump the review revision constantly, which is no conflict on the
  // item being touched. A 412 here means THIS item changed underneath.
  confirmWafrItem: (slug: string, playId: string, itemVersion: number, correctedText?: string) =>
    post<WafrReviewState>(
      slug,
      `/items/${playId}/confirm`,
      { corrected_text: correctedText ?? null },
      { itemVersion },
    ),

  answerWafrItem: (slug: string, playId: string, text: string, itemVersion: number) =>
    post<WafrReviewState>(slug, `/items/${playId}/answer`, { text }, { itemVersion }),

  wafrRemediationDone: (slug: string, playId: string, itemVersion: number) =>
    post<WafrReviewState>(slug, `/items/${playId}/remediation-done`, undefined, { itemVersion }),

  /** Answer a clarifying question the agent raised (appends a Q/A block to the
   *  item's notes). */
  answerWafrClarification: (
    slug: string,
    playId: string,
    clarificationId: string,
    text: string,
    itemVersion: number,
  ) =>
    post<WafrReviewState>(
      slug,
      `/items/${playId}/clarifications/${clarificationId}/answer`,
      { text },
      { itemVersion },
    ),

  /** Skip (remove) a clarifying question — no answer needed. */
  skipWafrClarification: (
    slug: string,
    playId: string,
    clarificationId: string,
    itemVersion: number,
  ) =>
    post<WafrReviewState>(
      slug,
      `/items/${playId}/clarifications/${clarificationId}/skip`,
      undefined,
      { itemVersion },
    ),

  /** Run the advisory analysis (202; result rides GET ''; 409 analysis_running). */
  kickWafrAnalysis: (slug: string, revision: number) =>
    post<{ analysis_status: string }>(slug, '/analysis', undefined, { ifMatch: revision }),

  /** Queue the agent's opening session turn. Idempotent server-side (a
   *  non-empty transcript / in-flight turn → noop), so it takes no revision. */
  kickWafrSession: (slug: string) => post<{ status: string }>(slug, '/session/kickoff'),

  /** Send a consultant chat message (409 while a turn is already running). */
  sendWafrSessionMessage: (slug: string, message: string, revision: number) =>
    post<{ status: string }>(slug, '/session/messages', { message }, { ifMatch: revision }),

  /** Download the deterministic WAFR report docx and trigger a save. */
  downloadWafrReport: async (slug: string): Promise<void> => {
    const blob = await requestBlob(`${base(slug)}/report.docx`)
    saveBlob(blob, `${slug}-wafr-review.docx`)
  },
}
